/**
 * Browser -> S3 upload flow (see docs/architecture.md "Uploads"):
 *   1. POST /sme/tasks/{id}/files          register, get presigned PUT URL
 *   2. PUT  <presigned url>                 upload bytes straight to S3 with progress
 *   3. POST /sme/tasks/{id}/files/{fid}/complete   confirm; backend validates content (state files)
 */
import { api } from './client'
import type { FileItem, FileKind } from '@/lib/types'

export interface UploadMeta {
  kind: FileKind
  criterion_id?: string
  description?: string
}

interface RegisterResponse { file: FileItem; upload_url: string; method: 'PUT' }

export function putWithProgress(url: string, file: File, contentType: string, onProgress?: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', contentType)
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100)) }
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Storage rejected the upload (${xhr.status}).`)))
    xhr.onerror = () => reject(new Error('Network error while uploading. Check your connection and try again.'))
    xhr.send(file)
  })
}

/** Uploads one file end to end. Resolves with the final file record (status uploaded or invalid). */
export async function uploadFile(taskId: string, file: File, meta: UploadMeta, onProgress?: (pct: number) => void): Promise<FileItem> {
  const contentType = file.type || 'application/octet-stream'
  const portal = meta.kind === 'model_results' ? 'eng' : 'sme'
  const reg = await api.post<RegisterResponse>(`/${portal}/tasks/${taskId}/files`, {
    ...meta, filename: file.name, size: file.size, content_type: contentType,
  })
  await putWithProgress(reg.upload_url, file, contentType, onProgress)
  const done = await api.post<{ file: FileItem }>(`/${portal}/tasks/${taskId}/files/${reg.file.file_id}/complete`, undefined, { acceptStatuses: [422] })
  return done.file
}

export const deleteFile = (taskId: string, fileId: string, kind?: FileKind) => api.del(`/${kind === 'model_results' ? 'eng' : 'sme'}/tasks/${taskId}/files/${fileId}`)

export async function downloadUrl(taskId: string, fileId: string): Promise<string> {
  const r = await api.get<{ url: string }>(`/tasks/${taskId}/files/${fileId}/download`)
  return r.url
}
